import { useState, useCallback, useEffect } from 'react';
import { Head } from '@inertiajs/react';
import { WandSparkles, TriangleAlert, CircleX } from 'lucide-react';
import { MainLayout } from '../../layouts/MainLayout';
import { useProgressBar } from '../../hooks/useProgressBar';
import { useShoppingList } from '../../hooks/useShoppingList';
import { ShoppingListForm } from '../../components/smart-list/ShoppingListForm';
import { ProgressIndicator } from '../../components/smart-list/ProgressIndicator';
import { SuccessResult } from '../../components/smart-list/SuccessResult';
import { Toast } from '../../components/smart-list/Toast';
import { updateCartBadge, dispatchCartCount } from '../../utils/cartBadge';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';

interface SmartListProps {
  isLocal: boolean;
  hasOpenAiKey: boolean;
}

type ViewState = 'form' | 'loading' | 'success' | 'error';

export default function SmartList({
  isLocal,
  hasOpenAiKey,
}: SmartListProps) {
  const [listText, setListText] = useState('');
  const [viewState, setViewState] = useState<ViewState>('form');
  const [showToast, setShowToast] = useState(false);

  const { progress, startProgress, completeProgress, resetProgress } = useProgressBar();
  const { submitList, result, error, reset: resetApiState, removeCartItem } = useShoppingList();

  const processList = useCallback(async (text: string) => {
    setViewState('loading');
    startProgress();

    try {
      await submitList(text);
      await completeProgress();
      setViewState('success');
      setListText('');
      updateCartBadge();
    } catch {
      await completeProgress();
      setViewState('error');
    }
  }, [submitList, startProgress, completeProgress]);

  const handleSubmit = useCallback(() => {
    if (!listText.trim()) {
      alert('Escribí lo que necesitás');
      return;
    }
    processList(listText);
  }, [listText, processList]);

  // Auto-process pending list from landing page
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('smart_list_pending');
      if (pending) {
        sessionStorage.removeItem('smart_list_pending');
        setListText(pending);
        processList(pending);
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (result && viewState === 'loading') {
      setViewState('success');
      setListText('');
    } else if (error && viewState === 'loading') {
      setViewState('error');
    }
  }, [result, error, viewState]);

  const handleNewList = useCallback(() => {
    setListText('');
    setViewState('form');
    resetProgress();
    resetApiState();
  }, [resetProgress, resetApiState]);

  const handleRemoveItem = useCallback(async (index: number) => {
    if (!result) return;
    const item = result.cart[index];

    // Remove from backend cart
    try {
      const res = await fetch(route('api.cart.remove_item'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ id: item.product_id, format_id: item.format_id }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.summary?.count != null) {
          dispatchCartCount(data.summary.count);
        }
      }
    } catch {
      // Continue with local removal even if backend fails
    }

    removeCartItem(index);
  }, [result, removeCartItem]);

  const handleShare = useCallback(async () => {
    if (!result?.cart_share_url) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'AbastoVirtual - Carrito', url: result.cart_share_url });
      } else {
        await navigator.clipboard.writeText(result.cart_share_url);
        setShowToast(true);
      }
    } catch (err) {
      console.error('Error sharing cart:', err);
    }
  }, [result]);

  const isLoading = viewState === 'loading';

  return (
    <MainLayout>
      <Head title="Armá tu carrito en segundos">
        <meta name="robots" content="noindex" />
      </Head>
      <div className="max-w-6xl mx-auto px-4 py-6 pb-16 md:pb-16 lg:pb-20">
        {/* Header */}
        <header className="text-center mb-8 md:mb-9 pb-5 border-b-2 border-gray-200">
          <h1 className="text-gray-800 text-2xl md:text-[28px] lg:text-[32px] font-bold m-0 mb-2">
            <WandSparkles className="inline w-6 h-6 md:w-7 md:h-7 text-green-600 mr-1 -mt-1" />
            Armá tu carrito en segundos
          </h1>
          <p className="text-gray-500 text-[15px] md:text-base leading-relaxed m-0">
            Decinos qué necesitás o qué vas a cocinar, como te salga.
            <br />
            Nosotros te armamos el carrito y después lo ajustás si querés.
          </p>
        </header>

        {/* Warning if OpenAI key is missing */}
        {!hasOpenAiKey && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded mb-5">
            <strong>
              <TriangleAlert className="inline w-4 h-4 mr-1 -mt-0.5" />
              Atención:
            </strong>
            {' '}Esta funcionalidad no está disponible en este momento.
          </div>
        )}

        {/* Show form only when idle */}
        {viewState === 'form' && (
          <ShoppingListForm
            value={listText}
            onChange={setListText}
            onSubmit={handleSubmit}
            onClear={handleNewList}
            isDisabled={false}
          />
        )}

        {isLoading && <ProgressIndicator progress={progress} />}

        {viewState === 'success' && result && (
          <SuccessResult
            result={result}
            isLocal={isLocal}
            onShare={handleShare}
            onRemoveItem={handleRemoveItem}
            onNewList={handleNewList}
          />
        )}

        {viewState === 'error' && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mt-5">
            <strong>
              <CircleX className="inline w-4 h-4 mr-1 -mt-0.5" />
              Error:
            </strong>
            {' '}{error}
            <div className="mt-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 bg-white text-red-600 border-2 border-red-300 py-2 px-5 rounded-md font-semibold text-sm cursor-pointer transition-all hover:bg-red-600 hover:text-white hover:border-red-600"
                onClick={handleNewList}
              >
                <WandSparkles className="w-4 h-4" />
                Intentar de nuevo
              </button>
            </div>
          </div>
        )}

        {showToast && (
          <Toast message="Link copiado al portapapeles" onClose={() => setShowToast(false)} />
        )}
      </div>
    </MainLayout>
  );
}
